/**
 * Boxes4U Premium Product Page
 *
 * Handles:
 * - Bundle-aware quantity plus/minus controls
 * - Manual quantity validation
 * - Case count and total-units summary
 * - Copy SKU action
 * - Variant-change synchronization where supported
 *
 * This file does not alter product pricing or shipping calculations.
 */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-b4u-product-main]';

  class B4UProductMain {
    constructor(root) {
      this.root = root;

      this.quantityWrapper = root.querySelector(
        '[data-b4u-quantity-wrapper]'
      );

      this.quantityInput = root.querySelector(
        '[data-b4u-quantity-input]'
      );

      this.minusButton = root.querySelector(
        '[data-b4u-quantity-minus]'
      );

      this.plusButton = root.querySelector(
        '[data-b4u-quantity-plus]'
      );

      this.caseCount = root.querySelector(
        '[data-b4u-case-count]'
      );

      this.totalUnits = root.querySelector(
        '[data-b4u-total-units]'
      );

      this.quantityError = root.querySelector(
        '[data-b4u-quantity-error]'
      );

      this.copySkuButton = root.querySelector(
        '[data-b4u-copy-sku]'
      );

      this.productForm = root.querySelector(
        '.b4u-product-main__form'
      );

      this.variantIdInput = root.querySelector(
        'input[name="id"]'
      );

      this.addToCartButton = root.querySelector(
        '[data-b4u-add-to-cart]'
      );

      this.addToCartButtonText = root.querySelector(
        '[data-b4u-add-button-text]'
      );

      this.productJsonElement = root.querySelector(
        '[data-b4u-product-json]'
      );

      this.stickyCart = root.querySelector(
        '[data-b4u-sticky-cart]'
      );

      this.stickyAddButton = root.querySelector(
        '[data-b4u-sticky-add]'
      );

      this.stickyCloseButton = root.querySelector(
        '[data-b4u-sticky-close]'
      );

      this.stickyMinusButton = root.querySelector(
        '[data-b4u-sticky-minus]'
      );

      this.stickyPlusButton = root.querySelector(
        '[data-b4u-sticky-plus]'
      );

      this.stickyQuantity = root.querySelector(
        '[data-b4u-sticky-quantity]'
      );

      this.productData = this.parseProductData();

      this.bindEvents();
      this.refreshQuantityState();
      this.updateStickyCartVisibility();
    }

    parseProductData() {
      if (!this.productJsonElement) {
        return null;
      }

      try {
        return JSON.parse(this.productJsonElement.textContent);
      } catch (error) {
        console.warn(
          'Boxes4U PDP: Product JSON could not be parsed.',
          error
        );

        return null;
      }
    }

    bindEvents() {
      if (this.minusButton) {
        this.minusButton.addEventListener('click', () => {
          this.changeQuantity(-1);
        });
      }

      if (this.plusButton) {
        this.plusButton.addEventListener('click', () => {
          this.changeQuantity(1);
        });
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener('input', () => {
          this.hideQuantityError();
          this.updateQuantitySummary();
          this.updateQuantityButtons();
        });

        this.quantityInput.addEventListener('change', () => {
          this.validateAndCorrectQuantity();
        });

        this.quantityInput.addEventListener('blur', () => {
          this.validateAndCorrectQuantity();
        });
      }

      if (this.productForm) {
        this.productForm.addEventListener('submit', (event) => {
          const isValid = this.validateAndCorrectQuantity();

          if (!isValid) {
            event.preventDefault();
            event.stopImmediatePropagation();

            if (this.quantityInput) {
              this.quantityInput.focus();
            }
          }
        });
      }

      if (this.copySkuButton) {
        this.copySkuButton.addEventListener('click', () => {
          this.copySku();
        });
      }

      /*
       * Theme integrations can dispatch different variant events.
       * These listeners are intentionally defensive.
       */
      this.root.addEventListener('variant:change', (event) => {
        this.handleVariantChange(event);
      });

      this.root.addEventListener('on:variant:change', (event) => {
        this.handleVariantChange(event);
      });

      document.addEventListener('variant:change', (event) => {
        if (this.eventBelongsToCurrentSection(event)) {
          this.handleVariantChange(event);
        }
      });

      document.addEventListener('on:variant:change', (event) => {
        if (this.eventBelongsToCurrentSection(event)) {
          this.handleVariantChange(event);
        }
      });

      window.addEventListener(
        'scroll',
        () => {
          this.updateStickyCartVisibility();
        },
        { passive: true }
      );

      if (this.stickyCloseButton) {
        this.stickyCloseButton.addEventListener('click', () => {
          if (this.stickyCart) {
            this.stickyCart.hidden = true;
            this.stickyCart.dataset.manuallyClosed = 'true';
          }
        });
      }

      if (this.stickyAddButton) {
        this.stickyAddButton.addEventListener('click', () => {
          if (this.productForm) {
            this.productForm.requestSubmit();
          }
        });
      }

      if (this.stickyMinusButton) {
        this.stickyMinusButton.addEventListener('click', () => {
          this.changeQuantity(-1);
          this.syncStickyQuantity();
        });
      }

      if (this.stickyPlusButton) {
        this.stickyPlusButton.addEventListener('click', () => {
          this.changeQuantity(1);
          this.syncStickyQuantity();
        });
      }
    }

    getStep() {
      if (!this.quantityWrapper) {
        return 1;
      }

      const step = Number(
        this.quantityWrapper.dataset.quantityStep
      );

      return Number.isFinite(step) && step > 0 ? step : 1;
    }

    getMinimum() {
      if (!this.quantityWrapper) {
        return 1;
      }

      const minimum = Number(
        this.quantityWrapper.dataset.minimumQuantity
      );

      return Number.isFinite(minimum) && minimum > 0
        ? minimum
        : 1;
    }

    getMaximum() {
      if (!this.quantityWrapper) {
        return null;
      }

      const maximumValue =
        this.quantityWrapper.dataset.maximumQuantity;

      if (
        maximumValue === undefined ||
        maximumValue === null ||
        maximumValue === ''
      ) {
        return null;
      }

      const maximum = Number(maximumValue);

      return Number.isFinite(maximum) && maximum >= 0
        ? maximum
        : null;
    }

    getCurrentQuantity() {
      if (!this.quantityInput) {
        return this.getMinimum();
      }

      const quantity = Number(this.quantityInput.value);

      return Number.isFinite(quantity)
        ? quantity
        : this.getMinimum();
    }

    changeQuantity(direction) {
      if (!this.quantityInput) {
        return;
      }

      const step = this.getStep();
      const minimum = this.getMinimum();
      const maximum = this.getMaximum();

      let nextQuantity =
        this.getCurrentQuantity() + step * direction;

      if (nextQuantity < minimum) {
        nextQuantity = minimum;
      }

      if (maximum !== null && nextQuantity > maximum) {
        nextQuantity = maximum;
      }

      /*
       * If inventory maximum is not a clean multiple of the step,
       * do not allow an invalid bundle quantity.
       */
      nextQuantity = this.getNearestValidQuantity(
        nextQuantity,
        direction
      );

      this.quantityInput.value = nextQuantity;

      this.hideQuantityError();
      this.updateQuantitySummary();
      this.updateQuantityButtons();

      this.quantityInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    getNearestValidQuantity(quantity, direction = 1) {
      const step = this.getStep();
      const minimum = this.getMinimum();
      const maximum = this.getMaximum();

      let correctedQuantity = Number(quantity);

      if (!Number.isFinite(correctedQuantity)) {
        correctedQuantity = minimum;
      }

      correctedQuantity = Math.max(
        correctedQuantity,
        minimum
      );

      const distanceFromMinimum =
        correctedQuantity - minimum;

      const stepCount =
        direction < 0
          ? Math.floor(distanceFromMinimum / step)
          : Math.ceil(distanceFromMinimum / step);

      correctedQuantity =
        minimum + Math.max(0, stepCount) * step;

      if (
        maximum !== null &&
        correctedQuantity > maximum
      ) {
        const maximumStepCount = Math.floor(
          (maximum - minimum) / step
        );

        correctedQuantity =
          minimum + Math.max(0, maximumStepCount) * step;
      }

      return Math.max(correctedQuantity, minimum);
    }

    isValidQuantity(quantity) {
      const step = this.getStep();
      const minimum = this.getMinimum();
      const maximum = this.getMaximum();

      if (!Number.isFinite(quantity)) {
        return false;
      }

      if (quantity < minimum) {
        return false;
      }

      if (maximum !== null && quantity > maximum) {
        return false;
      }

      return (quantity - minimum) % step === 0;
    }

    validateAndCorrectQuantity() {
      if (!this.quantityInput) {
        return true;
      }

      const enteredQuantity = Number(
        this.quantityInput.value
      );

      if (this.isValidQuantity(enteredQuantity)) {
        this.hideQuantityError();
        this.updateQuantitySummary();
        this.updateQuantityButtons();

        return true;
      }

      const correctedQuantity =
        this.getNearestValidQuantity(
          enteredQuantity,
          1
        );

      this.quantityInput.value = correctedQuantity;

      this.showQuantityError();
      this.updateQuantitySummary();
      this.updateQuantityButtons();

      /*
       * Quantity is corrected automatically, therefore the form
       * may submit safely after correction.
       */
      return true;
    }

    updateQuantitySummary() {
      const quantity = this.getCurrentQuantity();
      const step = this.getStep();

      if (this.totalUnits) {
        this.totalUnits.textContent =
          String(quantity);
      }

      if (this.caseCount) {
        const cases =
          step > 1 ? quantity / step : quantity;

        this.caseCount.textContent =
          Number.isInteger(cases)
            ? String(cases)
            : cases.toFixed(2);
      }

      this.syncStickyQuantity();
    }

    updateQuantityButtons() {
      const quantity = this.getCurrentQuantity();
      const minimum = this.getMinimum();
      const maximum = this.getMaximum();
      const step = this.getStep();

      if (this.minusButton) {
        this.minusButton.disabled =
          quantity <= minimum;
      }

      if (this.plusButton) {
        this.plusButton.disabled =
          maximum !== null &&
          quantity + step > maximum;
      }
    }

    refreshQuantityState() {
      if (!this.quantityInput) {
        return;
      }

      const correctedQuantity =
        this.getNearestValidQuantity(
          this.getCurrentQuantity(),
          1
        );

      this.quantityInput.value =
        correctedQuantity;

      this.updateQuantitySummary();
      this.updateQuantityButtons();
    }

    showQuantityError() {
      if (!this.quantityError) {
        return;
      }

      this.quantityError.hidden = false;

      window.clearTimeout(
        this.quantityErrorTimeout
      );

      this.quantityErrorTimeout =
        window.setTimeout(() => {
          this.hideQuantityError();
        }, 4500);
    }

    hideQuantityError() {
      if (this.quantityError) {
        this.quantityError.hidden = true;
      }
    }

    async copySku() {
      if (!this.copySkuButton) {
        return;
      }

      const sku =
        this.copySkuButton.dataset.sku || '';

      if (!sku.trim()) {
        this.setCopyButtonFeedback(
          'SKU unavailable'
        );

        return;
      }

      try {
        if (
          navigator.clipboard &&
          window.isSecureContext
        ) {
          await navigator.clipboard.writeText(sku);
        } else {
          this.copyTextFallback(sku);
        }

        this.setCopyButtonFeedback('SKU copied');
      } catch (error) {
        console.warn(
          'Boxes4U PDP: SKU could not be copied.',
          error
        );

        this.setCopyButtonFeedback(
          'Copy failed'
        );
      }
    }

    copyTextFallback(text) {
      const temporaryInput =
        document.createElement('textarea');

      temporaryInput.value = text;
      temporaryInput.setAttribute(
        'readonly',
        ''
      );

      temporaryInput.style.position =
        'fixed';

      temporaryInput.style.opacity = '0';

      document.body.appendChild(
        temporaryInput
      );

      temporaryInput.select();
      document.execCommand('copy');
      temporaryInput.remove();
    }

    setCopyButtonFeedback(message) {
      if (!this.copySkuButton) {
        return;
      }

      if (
        !this.copySkuButton.dataset.originalText
      ) {
        this.copySkuButton.dataset.originalText =
          this.copySkuButton.textContent.trim();
      }

      const originalText =
        this.copySkuButton.dataset.originalText;

      /*
       * Preserve the SVG and replace only the text node.
       */
      const textNodes = Array.from(
        this.copySkuButton.childNodes
      ).filter(
        (node) => node.nodeType === Node.TEXT_NODE
      );

      if (textNodes.length > 0) {
        textNodes[textNodes.length - 1].textContent =
          ` ${message}`;
      }

      window.clearTimeout(
        this.copyButtonTimeout
      );

      this.copyButtonTimeout =
        window.setTimeout(() => {
          const latestTextNodes = Array.from(
            this.copySkuButton.childNodes
          ).filter(
            (node) =>
              node.nodeType === Node.TEXT_NODE
          );

          if (latestTextNodes.length > 0) {
            latestTextNodes[
              latestTextNodes.length - 1
            ].textContent = ` ${originalText}`;
          }
        }, 2200);
    }

    updateStickyCartVisibility() {
      if (!this.stickyCart || !this.productForm) {
        return;
      }

      if (this.stickyCart.dataset.manuallyClosed === 'true') {
        return;
      }

      const formRect = this.productForm.getBoundingClientRect();
      const shouldShow = formRect.bottom < 0;

      this.stickyCart.hidden = !shouldShow;

      if (shouldShow) {
        this.syncStickyQuantity();
      }
    }

    syncStickyQuantity() {
      if (!this.stickyQuantity) {
        return;
      }

      this.stickyQuantity.textContent = String(
        this.getCurrentQuantity()
      );
    }

    eventBelongsToCurrentSection(event) {
      const detail = event?.detail;

      if (!detail) {
        return false;
      }

      const sectionId =
        this.root.dataset.sectionId;

      if (!sectionId) {
        return true;
      }

      return (
        detail.sectionId === sectionId ||
        detail.section_id === sectionId ||
        detail.section?.id === sectionId
      );
    }

    handleVariantChange(event) {
      const variant =
        event?.detail?.variant ||
        event?.detail?.selectedVariant ||
        event?.detail;

      if (
        !variant ||
        typeof variant !== 'object' ||
        !variant.id
      ) {
        return;
      }

      this.updateVariantId(variant);
      this.updateCopySku(variant);
      this.updateAddToCartState(variant);
    }

    updateVariantId(variant) {
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }
    }

    updateCopySku(variant) {
      if (!this.copySkuButton) {
        return;
      }

      this.copySkuButton.dataset.sku =
        variant.sku || '';
    }

    updateAddToCartState(variant) {
      if (!this.addToCartButton) {
        return;
      }

      const isAvailable =
        Boolean(variant.available);

      this.addToCartButton.disabled =
        !isAvailable;

      if (!this.addToCartButtonText) {
        return;
      }

      const addToCartText =
        this.addToCartButton.dataset
          .addToCartText || 'Add to cart';

      this.addToCartButtonText.textContent =
        isAvailable
          ? addToCartText
          : 'Sold out';
    }
  }

  const initialiseProductSections = (
    scope = document
  ) => {
    const roots = scope.querySelectorAll(
      ROOT_SELECTOR
    );

    roots.forEach((root) => {
      if (root.dataset.b4uInitialised === 'true') {
        return;
      }

      root.dataset.b4uInitialised = 'true';

      new B4UProductMain(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseProductSections()
    );
  } else {
    initialiseProductSections();
  }

  /*
   * Reinitialise sections loaded dynamically in Shopify Theme Editor.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseProductSections(
        event.target
      );
    }
  );
})();